import { useEffect, useRef, useState } from "react";
import { Camera, Check, X, Upload, Loader2, AtSign } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const fileInput = useRef(null);
  const [busy, setBusy] = useState(null); // "username" | "avatar" | null
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [username, setUsername] = useState("");
  const [editingUser, setEditingUser] = useState(false);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile/me");
        setProfile(data.user);
        setUsername(data.user.username || "");
        if (refresh) await refresh();
      } catch (e) { setError(formatErr(e)); }
    })();
  }, []);

  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(null), 4000); };

  const saveUsername = async (e) => {
    e.preventDefault();
    setBusy("username"); setError(null);
    try {
      const { data } = await api.patch("/profile/username", { username: username.trim().toLowerCase() });
      setProfile(data.user);
      setEditingUser(false);
      showFlash(t("profile.username_saved", "Username updated."));
      if (refresh) await refresh();
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(null); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError(t("profile.avatar_image_only", "Please choose an image file."));
      return;
    }
    setBusy("avatar"); setError(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/profile/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile(data.user);
      showFlash(t("profile.avatar_saved", "Avatar updated."));
      if (refresh) await refresh();
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(null); fileInput.current && (fileInput.current.value = ""); }
  };

  if (!profile) return <div className="pt-32 px-6 text-muted">{t("common.loading", "Loading…")}</div>;

  const avatarSrc = profile.avatar_url
    ? (profile.avatar_url.startsWith("http") ? profile.avatar_url : `${API_BASE}${profile.avatar_url}`)
    : null;

  return (
    <div className="pt-28 pb-24 px-5 md:px-10 mx-auto max-w-3xl">
      <div className="eyebrow flex items-center gap-3 mb-3">
        <span className={`role-badge ${profile.role}`}>{profile.role}</span> {t("profile.eyebrow", "Profile")}
      </div>
      <h1 className="section-title text-4xl md:text-5xl mb-2">
        {t("profile.title", "Your profile.")}
      </h1>
      <p className="text-sm text-muted mb-10">
        {t("profile.subtitle", "Set how the concierge desk and providers see you.")}
      </p>

      {error && <div className="text-rose-300 text-sm mb-4" data-testid="profile-error">{error}</div>}
      {flash && <div className="luxe-card p-4 text-sm text-teal border-teal/40 mb-4" data-testid="profile-flash">{flash}</div>}

      <div className="luxe-card p-6 mb-6">
        <div className="flex items-start gap-6 flex-wrap">
          <div className="relative">
            <div className="h-28 w-28 rounded-full bg-ink-deep border hairline overflow-hidden grid place-items-center" data-testid="avatar-preview">
              {avatarSrc ? (
                <img src={avatarSrc} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="font-serif-display text-3xl text-muted">
                  {(profile.name || profile.email)[0].toUpperCase()}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={busy === "avatar"}
              className="absolute -bottom-1 -right-1 h-9 w-9 grid place-items-center rounded-full bg-teal text-ink shadow-glow hover:bg-teal-hover transition disabled:opacity-60"
              aria-label="Change avatar"
              data-testid="avatar-upload-btn"
            >
              {busy === "avatar" ? <Loader2 size={14} className="animate-spin"/> : <Camera size={14}/>}
            </button>
            <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="avatar-file-input" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-serif-display text-2xl">{profile.name || "—"}</div>
            <div className="text-xs text-muted truncate">{profile.email}</div>
            <div className="text-xs text-muted/70 mt-2">
              {t("profile.avatar_hint", "JPG, PNG, WebP. Max 10 MB. Resized to 512px and optimized.")}
            </div>
          </div>
        </div>
      </div>

      <div className="luxe-card p-6">
        <div className="label-tiny mb-2 flex items-center gap-2"><AtSign size={12}/> {t("profile.username_label", "Username")}</div>
        {!editingUser ? (
          <div className="flex items-center justify-between gap-3">
            <div className="font-mono text-lg" data-testid="profile-username-display">@{profile.username || "…"}</div>
            <button onClick={() => setEditingUser(true)} className="btn btn-outline text-[12px]" data-testid="profile-edit-username">
              {t("profile.change_username", "Change")}
            </button>
          </div>
        ) : (
          <form onSubmit={saveUsername} className="flex items-center gap-2 flex-wrap">
            <span className="text-muted">@</span>
            <input
              required
              minLength={3}
              maxLength={24}
              pattern="[a-z0-9_]{3,24}"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="input-bare flex-1 min-w-[160px]"
              placeholder="lowercase_letters_123"
              data-testid="profile-username-input"
            />
            <button type="submit" disabled={busy === "username"} className="btn btn-primary text-[12px]" data-testid="profile-username-save">
              {busy === "username" ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>} {t("profile.save", "Save")}
            </button>
            <button type="button" onClick={() => { setEditingUser(false); setUsername(profile.username || ""); }} className="btn btn-ghost text-[12px]" data-testid="profile-username-cancel">
              <X size={12}/> {t("profile.cancel", "Cancel")}
            </button>
          </form>
        )}
        <div className="text-xs text-muted/70 mt-3">
          {t("profile.username_hint", "3–24 chars · lowercase letters, numbers, underscore only. This is how providers and other guests will find you.")}
        </div>
      </div>
    </div>
  );
}
